import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Layout, Menu } from "antd";
import SubMenu from "antd/lib/menu/SubMenu";
import { NavLink, useHistory } from "react-router-dom";
import styles from "./Sidebar.module.css";
import { compact, isFunction } from "lodash";
import { useTheme } from "themes/useTheme";
import Logo from "./components/Logo";
import styled from "themes/styled";
import { useIsAdmin, usePermissions } from "components/CurrentUser/hooks";
import { authorize } from "routes";
import { IRouteConfig } from "services/typings";
import querystring from "querystring";
import { useTranslation } from "react-i18next";
import { useFullscreen } from "components/AppConfig/hooks";

const { Sider } = Layout;

interface IProps {
  routes: any[];
  collapsed: boolean;
  pathName: string;
}

const Sidebar = (props: IProps) => {
  const { routes, collapsed, pathName } = props;
  const { push } = useHistory();
  const frameIdRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      !!frameIdRef.current && cancelAnimationFrame(frameIdRef.current);
    },
    []
  );

  const [openKeys, setOpenKeys] = useState([`/${pathName.split("/")[1]}`]);

  useEffect(() => {
    const animationFrameId = requestAnimationFrame(() => {
      const arrPathname = pathName.split("/");
      const openKeys = compact(arrPathname).reduce(
        (acc: string[], path: string, index) => {
          if (index === 0) acc.push(`/${path}`);
          if (index > 0) acc.push(`${acc[index - 1]}/${path}`);
          return acc;
        },
        []
      );
      setOpenKeys(openKeys);
    });
    return () => cancelAnimationFrame(animationFrameId);
  }, [pathName]);

  const selectedKeys = useMemo(
    () =>
      compact(pathName.split("/")).reduce(
        (acc: string[], path: string, index) => {
          if (index === 0) acc.push(`/${path}`);
          if (index > 0) acc.push(`${acc[index - 1]}/${path}`);
          return acc;
        },
        []
      ),
    [pathName]
  );

  const handleSubMenuClick = useCallback(
    ({ key, linkTo }: any) => {
      if (linkTo) {
        push(linkTo);
      }
      frameIdRef.current = requestAnimationFrame(() => {
        if (!collapsed) {
          setOpenKeys((openKeys) =>
            openKeys.includes(key)
              ? openKeys.filter((k) => k !== key)
              : [...openKeys, key]
          );
        }
      });
    },
    [collapsed, push]
  );

  const permissions = usePermissions();

  const isAdmin = useIsAdmin();

  const { t } = useTranslation();

  const renderMenuItem = (routes?: IRouteConfig[]) => {
    return routes?.map((route) => {
      const {
        name: _name,
        path,
        hideInMenu = false,
        icon: Icon,
        permissions: _permissions,
        component,
      } = route;
      // @ts-ignore
      const name = t(_name as any);
      if (hideInMenu || !authorize(permissions, _permissions, isAdmin))
        return null;
      const routes = (route?.routes || []).filter((r) => !r.hideInMenu);
      if (!routes.length) {
        if (!route.name) return null;
        return (
          <Menu.Item
            key={path as any}
            icon={!!Icon && <Icon style={{ fontSize: 18 }} />}
            title={name}
            onMouseEnter={() => {
              isFunction(component?.preload) && component?.preload();
            }}
          >
            <NavLink
              to={{
                pathname: path as any,
                search: !!route?.defaultSearch
                  ? typeof route?.defaultSearch === "string"
                    ? route.defaultSearch
                    : querystring.stringify(route?.defaultSearch as any)
                  : undefined,
              }}
            >
              <span style={{ userSelect: "none" }}>{name}</span>
            </NavLink>
          </Menu.Item>
        );
      } else {
        return (
          <SubMenu
            onTitleClick={({ key }) =>
              handleSubMenuClick({ key, linkTo: route.linkTo })
            }
            key={path as any}
            icon={!!Icon && <Icon style={{ fontSize: 18 }} />}
            title={name}
          >
            {renderMenuItem(route.routes)}
          </SubMenu>
        );
      }
    });
  };

  const [fullScreen] = useFullscreen();

  return (
    <StyledSider
      width={fullScreen ? 0 : 235}
      trigger={null}
      collapsible
      collapsed={fullScreen ? false : collapsed}
    >
      <Logo titleVisible={!collapsed} />
      <StyledMenu
        mode="inline"
        selectedKeys={selectedKeys}
        style={{ borderWidth: 0 }}
        {...(!collapsed ? { openKeys } : {})}
      >
        {renderMenuItem(routes)}
      </StyledMenu>
    </StyledSider>
  );
};

const StyledMenu = styled(Menu)`
  .ant-menu-item {
    height: 40px !important;
    line-height: 40px !important;
  }
  .ant-menu-submenu-title {
    height: 40px !important;
    line-height: 40px !important;
  }
`;

const StyledSider = styled(Sider)`
  background-color: ${(props) => props.theme.background_color} !important;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 2;
  overflow-x: hidden;
  overflow-y: auto;
  overflow: -moz-scrollbars-none;
  -ms-overflow-style: none;
  /* position: fixed !important; */
  min-height: 100vh;
  width: 230px;

  &::-webkit-scrollbar {
    width: 0px !important;
  }
`;

export default React.memo(Sidebar);
